import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RenderPreviewBody, renderEmailPreview } from '../api/campaignsApi';

interface EmailPreviewPanelProps {
  /** Debounced — typing in the form won't fire a request every keystroke. */
  body: RenderPreviewBody;
  /**
   * If supplied, the panel skips its own render call and just iframes
   * this HTML. Used by the campaign-detail "Preview" modal which
   * fetches the rendered HTML eagerly so the modal can open instantly.
   */
  html?: string;
  /** Set true to disable the auto-render (used while the parent form is invalid). */
  disabled?: boolean;
  /** Layout hint. Tall = stretch to fill parent column (modal preview pane). */
  variant?: 'tall' | 'compact';
}

type Viewport = 'desktop' | 'mobile';

const VIEWPORT_WIDTH: Record<Viewport, number> = {
  desktop: 640,
  mobile: 380,
};

/**
 * Renders the server-rendered campaign HTML in a sandboxed iframe so
 * the admin sees the email at full fidelity — fonts, dark-mode meta
 * tags, Outlook conditional comments, and CTA button styling all
 * intact. {@code srcDoc} (instead of {@code src}) avoids a blob URL,
 * keeps Content-Security-Policy simple, and ensures we don't leak
 * the rendered HTML to any external host.
 *
 * <p><b>Why iframe instead of {@code dangerouslySetInnerHTML}?</b>
 * The campaign HTML contains its own {@code <style>}, {@code <meta>}
 * (color-scheme!), and {@code @media} queries that should be scoped
 * to a single document. Inlining it directly would inherit the
 * dashboard's font stack and inadvertently let our admin CSS bleed
 * into the preview — exactly the opposite of what a preview is for.
 *
 * <p><b>Debounce.</b> The render endpoint is cheap (~30 ms server-
 * side) but typing into the subject/body inputs would still hammer
 * it without throttling. 350 ms feels live enough to keep the
 * preview obviously responsive without queuing up dozens of in-
 * flight requests.
 */
export const EmailPreviewPanel: React.FC<EmailPreviewPanelProps> = ({
  body,
  html: preloadedHtml,
  disabled,
  variant = 'tall',
}) => {
  const [html, setHtml] = useState<string | null>(preloadedHtml ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewport, setViewport] = useState<Viewport>('desktop');
  const reqIdRef = useRef(0);

  // Stable key for the debounce — derived from every field that
  // affects rendered HTML so we don't miss a change to e.g. CTA path.
  const renderKey = useMemo(() => {
    if (preloadedHtml) return 'preloaded';
    return JSON.stringify(body);
  }, [preloadedHtml, body]);

  useEffect(() => {
    if (preloadedHtml) {
      setHtml(preloadedHtml);
      return;
    }
    if (disabled) return;
    if (!body.subject?.trim() || !body.bodyText?.trim()) {
      setHtml(null);
      setError(null);
      return;
    }
    const id = ++reqIdRef.current;
    setLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const rendered = await renderEmailPreview(body);
        if (id !== reqIdRef.current) return;
        setHtml(rendered);
        setError(null);
      } catch (e) {
        if (id !== reqIdRef.current) return;
        setError(e instanceof Error ? e.message : 'Preview failed');
      } finally {
        if (id === reqIdRef.current) setLoading(false);
      }
    }, 350);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderKey, disabled]);

  const showSkeleton = !html && !error && (loading || !disabled);
  const isMobile = viewport === 'mobile';
  const tall = variant === 'tall';

  return (
    <div className="flex flex-col h-full min-h-[320px]">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50/70">
        <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
          <span className="inline-flex w-2 h-2 rounded-full bg-emerald-400" />
          <span>Live preview</span>
          {loading && <span className="text-gray-400">· rendering…</span>}
        </div>
        <div className="inline-flex p-0.5 bg-white border border-gray-200 rounded-md">
          <button
            type="button"
            onClick={() => setViewport('desktop')}
            className={`px-2 py-0.5 text-[11px] rounded ${
              !isMobile ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
            aria-pressed={!isMobile}
          >
            Desktop
          </button>
          <button
            type="button"
            onClick={() => setViewport('mobile')}
            className={`px-2 py-0.5 text-[11px] rounded ${
              isMobile ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
            aria-pressed={isMobile}
          >
            Mobile
          </button>
        </div>
      </div>

      {/* Preview surface */}
      <div
        className={`flex-1 overflow-auto bg-[radial-gradient(circle_at_top,rgba(148,194,220,0.12),transparent_60%)] bg-gray-100
          ${tall ? 'min-h-[360px]' : 'min-h-[280px]'}`}
      >
        <div className="w-full flex justify-center p-3 sm:p-5">
          <div
            style={{ width: VIEWPORT_WIDTH[viewport], maxWidth: '100%' }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-all"
          >
            {error ? (
              <div className="p-6 text-sm text-red-700 bg-red-50">
                Preview failed: {error}
              </div>
            ) : showSkeleton ? (
              <div className="p-6 space-y-3 animate-pulse">
                <div className="h-4 w-2/3 bg-gray-200 rounded" />
                <div className="h-3 w-1/2 bg-gray-200 rounded" />
                <div className="h-32 w-full bg-gray-100 rounded mt-4" />
                <div className="h-3 w-3/4 bg-gray-200 rounded mt-4" />
                <div className="h-3 w-1/2 bg-gray-200 rounded" />
              </div>
            ) : html ? (
              <iframe
                title="Email preview"
                srcDoc={html}
                sandbox="allow-same-origin"
                className="block w-full border-0"
                style={{ height: tall ? 'min(72vh, 820px)' : 480 }}
              />
            ) : (
              <div className="p-6 text-sm text-gray-400 text-center">
                Add a subject and body to see the live preview.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailPreviewPanel;
